import React, { useEffect, useState, useCallback } from 'react';
import { fmtWh } from './time';
import { authFetch, API } from './api';
import { detectZebraIfEnabled, deliverLabel } from './labelPrint';
import { useReprintApproval } from './pp2/ReprintApproval';
// The form itself lives in LabelForm.tsx, shared with the order panel's
// custom-label page — this file keeps only the Tools page shell and the
// printed-labels table. (Claude, 2026-08-30)
import LabelForm, { LabelFormOpts } from './LabelForm';

interface ManualRow {
  id: number; reference: string | null; carrier: string; delivery_type: string;
  tracking_number: string | null; tracking_alt?: string | null;
  receiver_name: string | null; weight_kg: number | null;
  printed_by: string | null; created_at: string | null;
  cancelled_at: string | null; cancelled_by: string | null; cancel_note: string | null;
  can_reprint: boolean; status: string | null; picked_up_at: string | null; delivered_at: string | null;
}

const fmt = (s: string | null) =>
  s ? (fmtWh(s, 'short') || '—') : '—';

export default function ManualLabelPage() {
  const [tab, setTab] = useState<'print' | 'printed'>('print');
  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-white">Print a shipping label</h1>
        <p className="text-gray-500 text-sm mt-0.5">Create a shipping label by hand for any carrier — no order needed.</p>
      </div>
      <div className="flex gap-1 border-b border-gray-800 mb-6">
        {(['print', 'printed'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              tab === t ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
            }`}>
            {t === 'print' ? 'Print a shipping label' : 'Printed labels'}
          </button>
        ))}
      </div>
      {tab === 'print' ? <PrintTab onPrinted={() => setTab('printed')} /> : <PrintedTab />}
    </div>
  );
}

function PrintTab({ onPrinted }: { onPrinted: () => void }) {
  const [opts, setOpts] = useState<LabelFormOpts | null>(null);

  useEffect(() => {
    authFetch(`${API}/manual-labels/options`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setOpts(d);
    });
  }, []);

  if (!opts) return <div className="text-gray-500 text-sm py-8">Loading…</div>;
  return (
    <LabelForm
      opts={opts}
      onSubmit={body => authFetch(`${API}/manual-labels/print`, {
        method: 'POST', body: JSON.stringify(body),
      })}
      onDone={onPrinted}
    />
  );
}


function PrintedTab() {
  const [rows, setRows] = useState<ManualRow[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const reprintApproval = useReprintApproval();     // a reprint by Tier 1 needs a manager's PIN or badge (19.10)
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/manual-labels`);
    if (r.ok) setRows(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const reprint = async (id: number) => {
    if (busy != null) return;
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing reprint…</p>');
    setBusy(id);
    try {
      const zebra = await detectZebraIfEnabled();
      const res = await reprintApproval.run(approver => authFetch(`${API}/manual-labels/${id}/reprint`, {
        method: 'POST', body: JSON.stringify({ format: zebra ? 'zpl' : 'pdf', ...approver }),
      }));
      if (!res) { if (win) win.close(); return; }        // the approval was cancelled
      const data = await res.json();
      if (!res.ok) { if (win) win.close(); alert(data.detail || 'Reprint failed'); return; }
      await deliverLabel(win, data, zebra);
    } catch { if (win) win.close(); } finally { setBusy(null); }
  };

  const cancel = async (row: ManualRow) => {
    if (busy != null) return;
    if (!window.confirm(`Cancel parcel ${row.tracking_number}?\n\nThe label stays here marked cancelled. The carrier is voided automatically only where supported — otherwise you'll be reminded to void it in the carrier portal.`)) return;
    setBusy(row.id);
    try {
      const res = await authFetch(`${API}/manual-labels/${row.id}/cancel`, { method: 'POST', body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Cancel failed'); return; }
      if (data.warning) alert(`Marked cancelled.\n\n⚠ ${data.warning}`);
      await load();
    } catch { alert('Cancel failed'); } finally { setBusy(null); }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Loading…</div>;
  if (rows.length === 0) return <div className="text-gray-500 text-sm py-8">No manually printed labels yet.</div>;

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
      {reprintApproval.box}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="px-3 py-2.5 text-left">#</th>
              <th className="px-3 py-2.5 text-left">Reference</th>
              <th className="px-3 py-2.5 text-left">Created</th>
              <th className="px-3 py-2.5 text-left">Carrier</th>
              <th className="px-3 py-2.5 text-left">Receiver</th>
              <th className="px-3 py-2.5 text-left">Picked up</th>
              <th className="px-3 py-2.5 text-left">Delivered</th>
              <th className="px-3 py-2.5 text-left">Tracking</th>
              <th className="px-3 py-2.5 text-left">By</th>
              <th className="px-3 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={`border-b border-gray-800/50 last:border-0 ${r.cancelled_at ? 'opacity-60' : ''}`}>
                <td className="px-3 py-2.5 text-gray-500">{rows.length - i}</td>
                <td className="px-3 py-2.5 text-white">{r.reference || '—'}</td>
                <td className="px-3 py-2.5 text-gray-400">{fmt(r.created_at)}</td>
                <td className="px-3 py-2.5 text-gray-300">{(r.carrier || '').replace('_', ' ')}<span className="text-gray-600 text-xs ml-1">{r.delivery_type === 'pp' ? '📦' : r.delivery_type === 'b2b' ? '🏢' : '🚚'}</span></td>
                <td className="px-3 py-2.5 text-gray-300">{r.receiver_name || '—'}</td>
                <td className="px-3 py-2.5 text-gray-500">{fmt(r.picked_up_at)}</td>
                <td className="px-3 py-2.5 text-gray-500">{fmt(r.delivered_at)}</td>
                <td className="px-3 py-2.5">
                  <span className={`font-mono text-xs ${r.cancelled_at ? 'text-gray-500 line-through' : 'text-blue-400'}`}>{r.tracking_number || '—'}</span>
                  {r.cancelled_at && <span className="ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-red-900/50 text-red-400">Cancelled</span>}
                  {r.tracking_alt && <div className="text-gray-500 text-[11px] font-mono">also: {r.tracking_alt}</div>}
                </td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{r.printed_by || '—'}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {!r.cancelled_at && (
                    <>
                      {r.can_reprint && (
                        <button disabled={busy === r.id} onClick={() => reprint(r.id)}
                          className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-200 mr-1">
                          {busy === r.id ? '…' : '🖨'}
                        </button>
                      )}
                      <button disabled={busy === r.id} onClick={() => cancel(r)}
                        className="text-xs px-2 py-1 rounded bg-red-900/40 hover:bg-red-800/60 disabled:opacity-50 text-red-400">
                        ✕
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
